import { useState, useEffect, useCallback } from "react";
import { Briefcase, Building2, MapPin, Calendar, Clock, Euro, Trash2, Edit, Plus, X, Save, StickyNote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "./adminApi";

const STATUS_META = {
  planned: { label: "Planifiée", cls: "bg-blue-100 text-blue-800 border-blue-300", dot: "bg-blue-500" },
  in_progress: { label: "En cours", cls: "bg-[#F0E9F3] text-[#662483] border-[#662483]/40", dot: "bg-[#662483]" },
  completed: { label: "Terminée", cls: "bg-green-100 text-green-800 border-green-300", dot: "bg-green-500" },
  cancelled: { label: "Annulée", cls: "bg-red-100 text-red-800 border-red-300", dot: "bg-red-500" },
};

function daysBetween(a, b) {
  if (!a) return 0;
  const start = new Date(a);
  const end = b ? new Date(b) : new Date();
  if (Number.isNaN(start.getTime())) return 0;
  const ms = end - start;
  return Math.max(0, Math.round(ms / 86400000));
}

function humanDuration(days) {
  if (days < 30) return `${days} j`;
  const months = Math.floor(days / 30);
  const rem = days % 30;
  if (months < 12) return `${months} mois${rem > 0 ? ` ${rem}j` : ""}`;
  const years = Math.floor(months / 12);
  const remM = months % 12;
  return `${years} an${years > 1 ? "s" : ""}${remM > 0 ? ` ${remM} mois` : ""}`;
}

function formatDateFR(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
}

const EMPTY = { company_name: "", job_title: "", start_date: "", end_date: "", status: "in_progress", location: "", hourly_rate: "", notes: "" };

/**
 * Full missions timeline + CRUD for a candidate (admin view).
 *
 * Props:
 *  - userId: string
 *  - candidateName: string (display only)
 */
export default function CandidateMissionsTimeline({ userId, candidateName = "" }) {
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // { ...mission } (existing) OR null (closed)
  const [saving, setSaving] = useState(false);

  const fetchMissions = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const r = await api.get(`/admin/candidates/${userId}/missions`);
      setMissions(r.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur chargement missions");
    } finally { setLoading(false); }
  }, [userId]);

  useEffect(() => { fetchMissions(); }, [fetchMissions]);

  const totalDays = missions.reduce((sum, m) => sum + daysBetween(m.start_date, m.end_date), 0);
  const openNew = () => setEditing({ ...EMPTY, _new: true });
  const openEdit = (m) => setEditing({ ...m });

  const saveMission = async () => {
    if (!editing) return;
    if (!editing.company_name || !editing.job_title || !editing.start_date) {
      toast.error("Entreprise, intitulé et date de début sont requis");
      return;
    }
    setSaving(true);
    const payload = {
      company_name: editing.company_name,
      job_title: editing.job_title,
      start_date: editing.start_date,
      end_date: editing.end_date || null,
      status: editing.status || "in_progress",
      location: editing.location || "",
      hourly_rate: editing.hourly_rate === "" || editing.hourly_rate === null ? null : Number(editing.hourly_rate),
      notes: editing.notes || "",
    };
    try {
      if (editing._new) {
        await api.post(`/admin/candidates/${userId}/missions`, payload);
        toast.success("Mission créée");
      } else {
        await api.patch(`/admin/missions/${editing.mission_id}`, payload);
        toast.success("Mission mise à jour");
      }
      setEditing(null);
      fetchMissions();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  const deleteMission = async (mission) => {
    if (!window.confirm(`Supprimer la mission chez ${mission.company_name} ?`)) return;
    try {
      await api.delete(`/admin/missions/${mission.mission_id}`);
      toast.success("Mission supprimée");
      fetchMissions();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="bg-white border border-gray-200 p-4" data-testid="missions-timeline">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h4 className="text-sm font-bold text-[#3F3F3F] flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-[#662483]" />
            Historique missions
          </h4>
          <p className="text-[10px] text-gray-500 mt-0.5">
            {missions.length} mission{missions.length > 1 ? "s" : ""}{" "}
            {totalDays > 0 && <>· {humanDuration(totalDays)} cumulé{totalDays > 30 ? "s" : ""}</>}
          </p>
        </div>
        <Button
          onClick={openNew}
          size="sm"
          className="rounded-none bg-gradient-to-r from-[#662483] to-[#0069B4] text-white text-xs h-8"
          data-testid="add-mission-btn"
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> Nouvelle mission
        </Button>
      </div>

      {loading && <p className="text-xs text-gray-500 italic">Chargement…</p>}

      {!loading && missions.length === 0 && (
        <div className="text-center py-6 bg-[#FAFAFA] border border-dashed border-gray-200" data-testid="missions-empty">
          <Briefcase className="w-8 h-8 mx-auto text-gray-300 mb-2" />
          <p className="text-xs text-gray-500">Aucune mission enregistrée pour {candidateName || "ce candidat"}.</p>
          <p className="text-[10px] text-gray-400 mt-1">Ajoutez la première mission pour retracer son parcours interim.</p>
        </div>
      )}

      {!loading && missions.length > 0 && (
        <div className="relative pl-6" data-testid="missions-list">
          {/* Vertical line */}
          <div className="absolute left-2 top-2 bottom-2 w-px bg-gray-200" aria-hidden="true" />
          {missions.map((m) => {
            const meta = STATUS_META[m.status] || STATUS_META.in_progress;
            const days = daysBetween(m.start_date, m.end_date);
            return (
              <div key={m.mission_id} className="relative pb-4" data-testid={`mission-item-${m.mission_id}`}>
                {/* Dot */}
                <span className={`absolute -left-4 top-2 w-3 h-3 rounded-full border-2 border-white shadow ${meta.dot}`} aria-hidden="true" />
                <div className={`border ${m.status === "in_progress" ? "border-[#662483]/40" : "border-gray-200"} p-3 hover:shadow-sm transition-shadow`}>
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Building2 className="w-3.5 h-3.5 text-[#662483]" />
                        <span className="text-sm font-bold text-[#3F3F3F]" data-testid={`mission-company-${m.mission_id}`}>{m.company_name}</span>
                        <Badge className={`rounded-none text-[10px] border ${meta.cls}`} data-testid={`mission-status-${m.mission_id}`}>{meta.label}</Badge>
                      </div>
                      <p className="text-xs text-gray-700 mt-1"><Briefcase className="w-3 h-3 inline mr-1 text-gray-400" />{m.job_title}</p>
                      <div className="flex items-center gap-3 text-[11px] text-gray-500 mt-2 flex-wrap">
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDateFR(m.start_date)}{m.end_date ? ` → ${formatDateFR(m.end_date)}` : " → en cours"}</span>
                        {days > 0 && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{humanDuration(days)}</span>}
                        {m.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{m.location}</span>}
                        {m.hourly_rate != null && <span className="flex items-center gap-1"><Euro className="w-3 h-3" />{m.hourly_rate}€/h</span>}
                      </div>
                      {m.notes && (
                        <div className="mt-2 text-[11px] text-gray-600 bg-[#FAFAFA] border-l-2 border-[#662483]/40 pl-2 py-1 flex gap-1" data-testid={`mission-notes-${m.mission_id}`}>
                          <StickyNote className="w-3 h-3 text-[#662483] mt-0.5 shrink-0" />
                          <span className="italic">{m.notes}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => openEdit(m)}
                        className="p-1.5 hover:bg-gray-100 text-[#0069B4]"
                        title="Modifier"
                        data-testid={`edit-mission-${m.mission_id}`}
                      >
                        <Edit className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => deleteMission(m)}
                        className="p-1.5 hover:bg-red-50 text-red-500"
                        title="Supprimer"
                        data-testid={`delete-mission-${m.mission_id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Edit / Create dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="sm:max-w-lg rounded-none" data-testid="mission-editor-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#3F3F3F]">
              {editing?._new ? "Nouvelle mission" : "Modifier la mission"}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3 mt-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold text-gray-700 mb-1 block">Entreprise *</Label>
                  <Input
                    value={editing.company_name}
                    onChange={(e) => setEditing({ ...editing, company_name: e.target.value })}
                    placeholder="XPO Logistics"
                    className="rounded-none text-sm"
                    data-testid="mission-form-company"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-700 mb-1 block">Intitulé du poste *</Label>
                  <Input
                    value={editing.job_title}
                    onChange={(e) => setEditing({ ...editing, job_title: e.target.value })}
                    placeholder="Cariste CACES 3"
                    className="rounded-none text-sm"
                    data-testid="mission-form-title"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold text-gray-700 mb-1 block">Date de début *</Label>
                  <Input
                    type="date"
                    value={editing.start_date}
                    onChange={(e) => setEditing({ ...editing, start_date: e.target.value })}
                    className="rounded-none text-sm"
                    data-testid="mission-form-start"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-700 mb-1 block">Date de fin</Label>
                  <Input
                    type="date"
                    value={editing.end_date || ""}
                    onChange={(e) => setEditing({ ...editing, end_date: e.target.value })}
                    className="rounded-none text-sm"
                    data-testid="mission-form-end"
                    placeholder="Vide si en cours"
                  />
                  <p className="text-[10px] text-gray-500 mt-0.5">Vide = mission en cours</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold text-gray-700 mb-1 block">Statut</Label>
                  <Select value={editing.status || "in_progress"} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                    <SelectTrigger className="rounded-none text-sm" data-testid="mission-form-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="planned">Planifiée</SelectItem>
                      <SelectItem value="in_progress">En cours</SelectItem>
                      <SelectItem value="completed">Terminée</SelectItem>
                      <SelectItem value="cancelled">Annulée</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-700 mb-1 block">Taux horaire (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editing.hourly_rate ?? ""}
                    onChange={(e) => setEditing({ ...editing, hourly_rate: e.target.value })}
                    placeholder="12.50"
                    className="rounded-none text-sm"
                    data-testid="mission-form-rate"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold text-gray-700 mb-1 block">Lieu</Label>
                <Input
                  value={editing.location || ""}
                  onChange={(e) => setEditing({ ...editing, location: e.target.value })}
                  placeholder="Lyon"
                  className="rounded-none text-sm"
                  data-testid="mission-form-location"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-gray-700 mb-1 block">Notes internes</Label>
                <Textarea
                  value={editing.notes || ""}
                  onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                  rows={3}
                  placeholder="Retour du client, commentaires internes..."
                  className="rounded-none text-sm"
                  data-testid="mission-form-notes"
                />
                <p className="text-[10px] text-gray-500 mt-0.5">Visible uniquement par les admins.</p>
              </div>
              <div className="flex gap-2 pt-1">
                <Button
                  onClick={saveMission}
                  disabled={saving}
                  className="bg-gradient-to-r from-[#662483] to-[#0069B4] text-white rounded-none flex-1 font-semibold"
                  data-testid="mission-form-save"
                >
                  <Save className="w-4 h-4 mr-2" /> {saving ? "Enregistrement..." : "Enregistrer"}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setEditing(null)}
                  disabled={saving}
                  className="rounded-none"
                  data-testid="mission-form-cancel"
                >
                  <X className="w-4 h-4 mr-1" /> Annuler
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
